import { useEffect, useCallback } from 'react';
import { Card, CardContent, Stack, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { API_URL } from '../api';
import { card, btn, ORDER_STATUS, badgeStyle } from '../styles/ds';
import { useAssistant } from './AssistantContext';

// Same TypeError-only network-failure detection as orders/OrderEdit.js:211 —
// fetch() only ever rejects with a TypeError for a genuine network failure
// (offline, DNS, CORS, connection reset), never for a definitive HTTP
// response. That's what distinguishes "no response was ever received" from
// any other error.
const isNetworkError = e => e instanceof TypeError;

const fmtCountdown = (s) => {
  const secs = Math.max(0, s);
  const m = Math.floor(secs / 60);
  const r = secs % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

/**
 * One proposal's full lifecycle, entirely self-contained.
 * States: pending | confirming | confirmed | declined | expired | stale |
 * missing_price | network_error | error.
 *
 * `proposal` = { token, order_id, current_status, new_status, expires_in }
 * as returned inline on the assistant reply that carried it (Story 1/3
 * response shape — see backend/controllers/assistant.js).
 */
const ProposalCard = ({ proposal }) => {
  const { token, order_id, current_status, new_status, expires_in } = proposal;
  const { proposalStates, setProposalState } = useAssistant();

  // Resolution state (state/secondsLeft/errorMessage) lives in
  // AssistantContext, keyed by this proposal's own token — not in local
  // useState — so it survives ProposalCard being unmounted and remounted
  // (closing/reopening the compact panel, expanding/minimizing to the full
  // page): the conversation must stay "exactly as it was left."
  const initialSecondsLeft = Number.isFinite(expires_in) ? expires_in : 0;
  const saved = proposalStates[token];
  const state = saved ? saved.state : 'pending';
  const secondsLeft = saved ? saved.secondsLeft : initialSecondsLeft;
  const errorMessage = saved ? saved.errorMessage : '';

  // Seed shared state the first time this token is ever seen.
  useEffect(() => {
    if (!proposalStates[token]) {
      setProposalState(token, { state: 'pending', secondsLeft: initialSecondsLeft, errorMessage: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Countdown — client clock only, purely for UX. The server is still
  // authoritative on confirm: an expired-server-side click still comes back
  // as a 401 'expired', mapped below to this same 'expired' state.
  useEffect(() => {
    if (state !== 'pending') return undefined;
    if (secondsLeft <= 0) { setProposalState(token, { state: 'expired' }); return undefined; }
    const t = setTimeout(() => setProposalState(token, prev => ({ secondsLeft: prev.secondsLeft - 1 })), 1000);
    return () => clearTimeout(t);
  }, [state, secondsLeft, token, setProposalState]);

  const handleConfirm = useCallback(async () => {
    setProposalState(token, { state: 'confirming', errorMessage: '' });

    // Deliberately NOT authFetch: a 401 here means the *proposal token*
    // expired (mapped to the 'expired' state below), not that the admin's
    // login session is invalid — authFetch now hard-redirects to /login on
    // any 401, which would be wrong here. Same auth-header source
    // (authFetch uses), no redirect side effect. See stories/4's Design
    // Notes ("The authFetch 401-redirect conflict").
    const confirmOnce = () => {
      const authToken = localStorage.getItem('auth_token');
      const headers = new Headers({ 'Content-Type': 'application/json' });
      if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
      return fetch(`${API_URL}/assistant/confirm`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token }),
      });
    };

    let res;
    try {
      try {
        res = await confirmOnce();
      } catch (e) {
        if (!isNetworkError(e)) throw e;
        // Genuinely uncertain outcome — one automatic retry, same token.
        // Story 3's idempotency guarantees a retry of the same token never
        // re-executes, so this is safe.
        res = await confirmOnce();
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // Both attempts failed at the network level — surface a manual
        // "try again" rather than a generic error; the same token is safe
        // to retry indefinitely.
        setProposalState(token, { state: 'network_error' });
      } else {
        console.error(e);
        setProposalState(token, { errorMessage: e?.message || 'שגיאה באישור הפעולה', state: 'error' });
      }
      return;
    }

    const body = await res.json().catch(() => ({}));
    if (res.ok) { setProposalState(token, { state: 'confirmed' }); return; }

    // A 401 with no `code` field didn't come from the proposal-confirm logic
    // at all — it's `requireAuth` rejecting because the admin's own login
    // session has expired, before confirm ever ran. Every other authenticated
    // action in the app (via authFetch) redirects to /login on this; mirror
    // that here, since this call deliberately bypasses authFetch (see the
    // comment above confirmOnce). Every code-bearing 401 (proposal-token
    // expired, stale, etc.) still falls through to the switch below exactly
    // as before.
    if (res.status === 401 && !body.code) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
      return;
    }

    switch (body.code) {
      case 'expired':
        setProposalState(token, { state: 'expired' });
        break;
      case 'stale':
        setProposalState(token, { state: 'stale' });
        break;
      case 'missing_price':
        setProposalState(token, { errorMessage: body.error || '', state: 'missing_price' });
        break;
      default:
        // wrong_user / invalid_token / idempotency_key_reused / anything
        // else — none reachable through normal use of this UI (a proposal
        // token is only ever received and used within the same admin's own
        // browser session). Falls to the generic error state honestly,
        // without inventing UX for an unreachable path.
        setProposalState(token, { errorMessage: body.error || 'שגיאה באישור הפעולה', state: 'error' });
    }
  }, [token, setProposalState]);

  const handleDecline = () => setProposalState(token, { state: 'declined' });

  const currentMeta = ORDER_STATUS[current_status] || { label: current_status };
  const newMeta = ORDER_STATUS[new_status] || { label: new_status };

  const renderBody = () => {
    switch (state) {
      case 'pending':
      case 'confirming':
        return (
          <Stack className="crm-wrap" direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography fontSize={13}>הזמנה #{order_id}:</Typography>
            <span style={badgeStyle(ORDER_STATUS, current_status)}>{currentMeta.label}</span>
            <Typography fontSize={13}>&larr;</Typography>
            <span style={badgeStyle(ORDER_STATUS, new_status)}>{newMeta.label}</span>
          </Stack>
        );
      case 'confirmed':
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
            <Typography fontSize={13} color="#2e7d32">
              הזמנה #{order_id}: הסטטוס עודכן ל"{newMeta.label}" בהצלחה
            </Typography>
          </Stack>
        );
      case 'declined':
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <CancelIcon sx={{ color: '#666', fontSize: 18 }} />
            <Typography fontSize={13} color="text.secondary">ההצעה נדחתה</Typography>
          </Stack>
        );
      case 'expired':
        return (
          <Typography fontSize={13} color="#856404">
            תוקף ההצעה פג — יש לבקש שוב
          </Typography>
        );
      case 'stale':
        return (
          <Typography fontSize={13} color="#c62828">
            ההזמנה השתנתה מאז שהוצעה — יש לבקש שוב
          </Typography>
        );
      case 'missing_price':
        return <Typography fontSize={13} color="#c62828">{errorMessage}</Typography>;
      case 'network_error':
        return (
          <Typography fontSize={13} color="#c62828">
            בעיית תקשורת — לא ניתן היה לאשר את הפעולה
          </Typography>
        );
      case 'error':
      default:
        return <Typography fontSize={13} color="#c62828">{errorMessage || 'אירעה שגיאה באישור הפעולה'}</Typography>;
    }
  };

  const buttonsDisabled = state !== 'pending';

  return (
    <Card sx={{ ...card.highlight, mt: 1, maxWidth: 480 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.25}>
          {renderBody()}

          {state === 'pending' && (
            <Typography fontSize={11} color="text.secondary">
              פג תוקף ההצעה בעוד {fmtCountdown(secondsLeft)}
            </Typography>
          )}

          {state === 'network_error' ? (
            <Stack direction="row">
              <Button size="small" variant="outlined" onClick={handleConfirm} sx={btn.gold}>
                נסה שוב
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={buttonsDisabled}
                onClick={handleConfirm}
                startIcon={state === 'confirming' ? <CircularProgress size={14} color="inherit" /> : null}
                sx={btn.primary}
              >
                אישור
              </Button>
              <Button size="small" variant="outlined" disabled={buttonsDisabled} onClick={handleDecline} sx={btn.cancel}>
                דחייה
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProposalCard;
